import { Editor, Element, Node, Text } from 'slate';

import {
  backfillLocationId,
  doInsideNormalization,
  isDocumentEntry,
  isDynamicBlockEntry,
  isFooterElement,
  isFootersPartEntry,
  isHeaderElement,
  isHeadersPartEntry,
  isLocationIdElement,
  isParagraphEntry,
  isTableCellEntry,
  isTableEntry,
  isTableRowEntry,
  isTOCEntry,
  normalizeDocument,
  normalizeDynamicBlock,
  normalizeEditor,
  normalizeFooter,
  normalizeFootersPart,
  normalizeHeader,
  normalizeHeadersPart,
  normalizeInline,
  normalizeParagraph,
  normalizeTable,
  normalizeTableCell,
  normalizeTableRow,
  normalizeText,
  normalizeTOC,
  removeEmptySuggestionMarkers,
  validateParagraphProps,
} from '../internal';

/**
 * Note: It's important for withValidation to be called before withPagination in the plugin stack.
 */
export function withValidation<T extends Editor>(
  editor: T,
  options: { callNextNormalization?: boolean } = {}
): T {
  const { callNextNormalization = true } = options;
  const { normalizeNode } = editor;

  editor.normalizeNode = (entry): void => {
    doInsideNormalization(editor, () => {
      const [node, path] = entry;
      const pathRef = Editor.pathRef(editor, path);

      if (Editor.isEditor(node)) {
        normalizeEditor(editor);
        removeEmptySuggestionMarkers(editor);
      } else if (isDocumentEntry(entry)) {
        normalizeDocument(editor, entry);
      } else if (isHeadersPartEntry(entry)) {
        normalizeHeadersPart(editor, entry);
      } else if (isFootersPartEntry(entry)) {
        normalizeFootersPart(editor, entry);
      } else if (isHeaderElement(node)) {
        normalizeHeader(editor, [node, path]);
      } else if (isFooterElement(node)) {
        normalizeFooter(editor, [node, path]);
      } else if (isTableEntry(entry)) {
        normalizeTable(editor, entry);
      } else if (isTableRowEntry(entry)) {
        normalizeTableRow(editor, entry);
      } else if (isTableCellEntry(entry)) {
        normalizeTableCell(editor, entry);
      } else if (isTOCEntry(entry)) {
        normalizeTOC(editor, entry);
      } else if (isDynamicBlockEntry(entry)) {
        normalizeDynamicBlock(editor, entry);
      } else if (isParagraphEntry(entry)) {
        const validatedEntry = validateParagraphProps(editor, entry);

        normalizeParagraph(editor, validatedEntry);
      } else if (Element.isElement(node) && Editor.isInline(editor, node)) {
        normalizeInline(editor, path);
      } else if (Text.isText(node)) {
        normalizeText(editor, [node, path]);
      }

      const newPath = pathRef.unref();
      if (!newPath || !Node.has(editor, newPath)) return;

      const [updatedNode] = Editor.node(editor, newPath);
      if (isLocationIdElement(updatedNode)) backfillLocationId(editor, [updatedNode, newPath]);

      if (callNextNormalization) {
        const updatedEntry = Editor.node(editor, newPath);
        normalizeNode(updatedEntry);
      }
    });
  };

  return editor;
}
